import React, { useState, useEffect, useMemo } from 'react';
import { Sliders, Star, RotateCcw, Save, Search, Info } from 'lucide-react';
import { geoAnalyticsAPI, categoryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const INPUT = { padding: '7px 10px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13.5, fontFamily: 'var(--font-sans)', width: '100%', boxSizing: 'border-box' };

export default function MarketProfiles() {
  useAuth();
  const [data, setData] = useState(null);          // { profiles, states, brands }
  const [selKey, setSelKey] = useState('base');
  const [edits, setEdits] = useState(null);        // working copy { label, keywords, def, mult }
  const [tab, setTab] = useState('weights');
  const [brandQ, setBrandQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);

  const load = () => geoAnalyticsAPI.profiles()
    .then(r => setData(r.data.data || r.data))
    .catch(e => setErr(e.response?.data?.message || 'Failed to load market profiles'));
  useEffect(() => { load(); }, []);

  const profile = useMemo(() => data?.profiles?.find(p => p.key === selKey) || null, [data, selKey]);
  useEffect(() => {
    if (profile) setEdits({ label: profile.label || '', keywords: profile.keywords || '', def: profile.def, mult: { ...(profile.mult || {}) } });
  }, [profile]);

  const isBase = selKey === 'base';
  const isPremiumProfile = selKey === 'premium';
  const cap = isBase ? 100 : 10;

  const setMult = (state, raw) => {
    setEdits(e => {
      const mult = { ...e.mult };
      if (raw === '' || raw === null) delete mult[state];
      else mult[state] = raw;
      return { ...e, mult };
    });
  };

  const save = () => {
    if (!edits) return;
    setSaving(true); setMsg(''); setErr('');
    const mult = {};
    for (const [state, v] of Object.entries(edits.mult)) {
      const n = Number(v);
      if (v !== '' && isFinite(n) && n >= 0 && n <= cap) mult[state] = n;
    }
    const body = { label: edits.label, mult };
    if (!isBase) { body.keywords = edits.keywords; body.def = Number(edits.def); }
    geoAnalyticsAPI.updateProfile(selKey, body)
      .then(() => { setMsg('Saved — Geo Analytics uses the new weights immediately.'); load(); })
      .catch(e => setErr(e.response?.data?.message || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const resetAll = () => {
    if (!confirmReset) { setConfirmReset(true); setTimeout(() => setConfirmReset(false), 4000); return; }
    setConfirmReset(false); setSaving(true); setMsg(''); setErr('');
    geoAnalyticsAPI.resetProfiles()
      .then(() => { setMsg('All profiles restored to the seeded analytical baseline.'); load(); })
      .catch(e => setErr(e.response?.data?.message || 'Reset failed'))
      .finally(() => setSaving(false));
  };

  const togglePremium = (b) => {
    // optimistic flip; Brand PUT persists it and geo analytics reads the flag
    setData(d => ({ ...d, brands: d.brands.map(x => x._id === b._id ? { ...x, isPremium: !b.isPremium } : x) }));
    categoryAPI.updateBrand(b._id, { isPremium: !b.isPremium })
      .catch(() => { setErr(`Could not update ${b.name}`); load(); });
  };

  if (!data && err) return <div style={{ padding: 24 }}><div className="card" style={{ padding: 24, borderLeft: `4px solid ${RED}`, color: RED }}>{err}</div></div>;
  if (!data) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading market profiles…</div>;

  const brands = (data.brands || []).filter(b => !brandQ || b.name.toLowerCase().includes(brandQ.toLowerCase()));
  const premiumCount = (data.brands || []).filter(b => b.isPremium).length;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
          <Sliders size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />Market Profiles
        </h1>
        <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500, maxWidth: 860, lineHeight: 1.55 }}>
          Your GTM blueprint's engine: these weights drive the <b style={{ color: CHAR }}>Expansion Targets</b> view in Geo Analytics.
          Seeded with our analytical baseline (GSDP/consumption estimates) — tune them as your market research sharpens.
          <b style={{ color: CHAR }}> Expansion score = state index × segment multiplier × premium skew ÷ (1 + vendors onboarded).</b>
        </p>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {[{ k: 'weights', t: 'Market Weights' }, { k: 'brands', t: `Premium Brands (${premiumCount})` }].map(x => (
          <button key={x.k} onClick={() => setTab(x.k)} style={{
            padding: '9px 18px', borderRadius: 20, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, fontFamily: 'var(--font-sans)',
            border: `1.5px solid ${tab === x.k ? GREEN : BORDER}`, background: tab === x.k ? GREEN : '#fff', color: tab === x.k ? '#fff' : MUTED,
          }}>{x.t}</button>
        ))}
      </div>

      {msg && <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 10, background: 'rgba(27,92,58,0.08)', color: GREEN, fontSize: 13.5, fontWeight: 600 }}>{msg}</div>}
      {err && <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 10, background: 'rgba(220,38,38,0.07)', color: RED, fontSize: 13.5, fontWeight: 600 }}>{err}</div>}

      {tab === 'weights' && edits && (
        <div className="card" style={{ padding: 28 }}>
          {/* profile selector */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {data.profiles.map(p => (
              <button key={p.key} onClick={() => setSelKey(p.key)} style={{
                padding: '7px 14px', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-sans)',
                border: `1.5px solid ${selKey === p.key ? PURPLE : BORDER}`,
                background: selKey === p.key ? PURPLE : '#fff', color: selKey === p.key ? '#fff' : MUTED,
              }}>{p.key === 'base' ? '⚖ Base index' : p.key === 'premium' ? '★ Premium skew' : (p.label || p.key)}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', marginBottom: 20, borderRadius: 10, background: 'rgba(37,99,235,0.06)', fontSize: 13, color: CHAR, fontWeight: 500, lineHeight: 1.5 }}>
            <Info size={17} style={{ color: '#2563eb', flexShrink: 0, marginTop: 1 }} />
            <span>
              {isBase
                ? 'Base index: the absolute market size of each state, 0–100 (100 = biggest market). Every segment starts from these numbers.'
                : isPremiumProfile
                ? 'Premium skew: multipliers applied ON TOP of a segment when the selected brand is tagged Premium (see the Premium Brands tab). >1 boosts a state, <1 damps it.'
                : 'Segment multipliers over the base index. >1 = this segment over-indexes in that state, <1 = under-indexes. "Keywords" is the (case-insensitive) pattern matched against the selected category/sub-category/brand names to activate this profile.'}
            </span>
          </div>

          {/* meta fields */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ minWidth: 260, flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>Label</label>
              <input style={INPUT} value={edits.label} onChange={e => setEdits({ ...edits, label: e.target.value })} />
            </div>
            {!isBase && (
              <div style={{ minWidth: 130, width: 150 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>Default × (unlisted states)</label>
                <input style={INPUT} type="number" step="0.05" min="0" max="5" value={edits.def} onChange={e => setEdits({ ...edits, def: e.target.value })} />
              </div>
            )}
            {!isBase && !isPremiumProfile && (
              <div style={{ minWidth: 320, flex: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>Keywords (regex, matches cat/sub/brand names)</label>
                <input style={{ ...INPUT, fontFamily: 'monospace', fontSize: 12.5 }} value={edits.keywords} onChange={e => setEdits({ ...edits, keywords: e.target.value })} />
              </div>
            )}
          </div>

          {/* states grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10, marginBottom: 24 }}>
            {data.states.map(state => {
              const v = edits.mult[state];
              const listed = v !== undefined && v !== '';
              return (
                <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 9, background: listed ? '#fff' : '#f7f7f5', border: `1px solid ${listed ? GOLD + '66' : BORDER}` }}>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: listed ? CHAR : MUTED }}>{state}</span>
                  <input
                    style={{ ...INPUT, width: 72, padding: '5px 8px', textAlign: 'right' }}
                    type="number" step={isBase ? 1 : 0.05} min="0" max={cap}
                    placeholder={isBase ? '0' : `${edits.def}×`}
                    value={v ?? ''}
                    onChange={e => setMult(state, e.target.value)}
                  />
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 9, border: 'none', cursor: 'pointer', background: GREEN, color: '#fff', fontSize: 14, fontWeight: 700, opacity: saving ? 0.6 : 1 }}>
              <Save size={16} />{saving ? 'Saving…' : `Save ${isBase ? 'base index' : 'profile'}`}
            </button>
            <button onClick={resetAll} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 9, border: `1.5px solid ${confirmReset ? RED : BORDER}`, cursor: 'pointer', background: confirmReset ? 'rgba(220,38,38,0.08)' : '#fff', color: confirmReset ? RED : MUTED, fontSize: 13.5, fontWeight: 700 }}>
              <RotateCcw size={15} />{confirmReset ? 'Click again — resets ALL profiles' : 'Reset all to baseline'}
            </button>
            <span style={{ fontSize: 12, color: MUTED, fontWeight: 500 }}>Changes apply to Geo Analytics within a minute (usually instantly).</span>
          </div>
        </div>
      )}

      {tab === 'brands' && (
        <div className="card" style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: 0 }}>Premium brands</h3>
            <div style={{ position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: 10, top: 9, color: MUTED }} />
              <input style={{ ...INPUT, width: 240, paddingLeft: 32 }} placeholder="Search brands…" value={brandQ} onChange={e => setBrandQ(e.target.value)} />
            </div>
          </div>
          <p style={{ fontSize: 13, color: MUTED, margin: '0 0 18px', fontWeight: 500, maxWidth: 760, lineHeight: 1.5 }}>
            Premium-tagged brands get the metro skew in Geo Analytics (Mumbai/Bengaluru/Delhi weigh heavier). Obvious ones (Apple, BMW…) were auto-tagged from their names — tag or untag the rest yourself; the flag on the brand is authoritative.
          </p>
          {brands.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: MUTED, fontSize: 14, fontWeight: 500 }}>No brands{brandQ ? ' match the search' : ' yet'}.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
              {brands.map(b => (
                <button key={b._id} onClick={() => togglePremium(b)} title={b.isPremium ? 'Click to remove premium tag' : 'Click to tag as premium'} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                  border: `1.5px solid ${b.isPremium ? GOLD : BORDER}`, background: b.isPremium ? 'rgba(245,166,35,0.10)' : '#fff',
                }}>
                  <Star size={16} style={{ color: b.isPremium ? GOLD : '#d1d5db', fill: b.isPremium ? GOLD : 'none', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: CHAR, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                  {b.isActive === false && <span style={{ fontSize: 10, color: MUTED, fontWeight: 700 }}>INACTIVE</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <p style={{ fontSize: 11.5, color: MUTED, marginTop: 20, textAlign: 'center' }}>
        Weights are estimates you own — they are the market-baseline input to Geo Analytics' Expansion view, not live market data.
      </p>
    </div>
  );
}
